import { createPresignedUpload } from '@/actions/files/presign'

export const IMAGE_UPLOAD_ERROR_MESSAGE =
  'Image upload failed due to a network or storage issue. Please try again.'

export function isImageUploadError(error) {
  return error instanceof Error && error.message === IMAGE_UPLOAD_ERROR_MESSAGE
}

/**
 * Uploads an image to S3 from a file
 * @param {Object} params - The parameters for the upload
 * @param {File} params.img - The image file to upload
 * @param {string} params.key - The S3 key to use for the upload
 * @returns {Promise<Object>} - The S3 upload response
 */
export async function uploadImageFromFile({ img, key }) {
  if (!img) {
    throw new Error('Missing file for upload')
  }

  const contentType = img.type || 'application/octet-stream'
  let lastError

  // Retry once for transient network failures when uploading directly from the browser.
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      const presigned = await createPresignedUpload({ key, contentType })
      const response = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: img,
        headers: {
          'Content-Type': contentType,
        },
      })

      if (!response.ok) {
        lastError = new Error('Failed to upload image to S3')
        continue
      }

      return presigned.metadata
    } catch (error) {
      lastError = error
    }
  }

  const uploadError = new Error(IMAGE_UPLOAD_ERROR_MESSAGE)
  uploadError.cause = lastError
  throw uploadError
}
